import { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, Card, CardContent, Grid } from '@mui/material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import { GalaxyMap } from '../console/GalaxyMap';
import { SystemDetailsPanel } from './SystemDetailsPanel';
import type { GalaxyMapData, Player, StarSystem } from '../../types/gameState';

interface MapPanelProps {
  playerId: string;
}

export function MapPanel({ playerId }: MapPanelProps) {
  const [mapData, setMapData] = useState<GalaxyMapData | null>(null);
  const [currentSystemId, setCurrentSystemId] = useState<string | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<StarSystem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMapData() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch player to get current system ID
        const playerLogId = logService.api('GET', `/api/players/${playerId}`, {}, 'pending');
        const playerStart = Date.now();
        const playerRes = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}`);
        const playerDuration = Date.now() - playerStart;

        if (!playerRes.ok) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: `HTTP ${playerRes.status}` });
        } else {
          const playerResult = await playerRes.json();
          if (playerResult.success && playerResult.data) {
            logService.updateLog(playerLogId, { status: 'success', duration: playerDuration });
            setCurrentSystemId((playerResult.data as Player).current_system_id);
          } else {
            logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: 'No player data' });
          }
        }

        // Fetch galaxy map data
        const mapLogId = logService.api('GET', '/api/map', {}, 'pending');
        const mapStart = Date.now();
        const mapRes = await fetch(`${DATABASE_SERVICE_URL()}/api/map`);
        const mapDuration = Date.now() - mapStart;

        if (!mapRes.ok) {
          logService.updateLog(mapLogId, { status: 'error', duration: mapDuration, message: `HTTP ${mapRes.status}` });
          setError('Failed to load galaxy map');
          setIsLoading(false);
          return;
        }

        const mapResult = await mapRes.json();
        if (!mapResult.success || !mapResult.data) {
          logService.updateLog(mapLogId, { status: 'error', duration: mapDuration, message: 'No map data' });
          setError('No galaxy map data available');
          setIsLoading(false);
          return;
        }

        logService.updateLog(mapLogId, { status: 'success', duration: mapDuration });
        setMapData(mapResult.data);
      } catch (err) {
        setError('Failed to load galaxy map');
      } finally {
        setIsLoading(false);
      }
    }

    fetchMapData();
  }, [playerId]);

  if (isLoading) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider', height: '100%' }}>
            <CardContent sx={{ p: 2, height: '100%' }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="rectangular" height="calc(100% - 40px)" sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="error" align="center">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  const handleSystemSelect = (system: StarSystem) => {
    setSelectedSystem(system);
  };

  const handleCloseDetails = () => {
    setSelectedSystem(null);
  };

  return (
    <Box
      className="panel"
      sx={{
        padding: 0,
        gap: 0,
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          padding: 0
        }}
      >
        {selectedSystem ? (
          <Grid container spacing={2} sx={{ flex: 1, height: '100%', overflow: 'hidden' }}>
            {/* Hide map on mobile when details are shown, show on desktop */}
            <Grid item xs={12} md={7} sx={{ height: '100%', display: { xs: 'none', md: 'flex' }, flexDirection: 'column' }}>
              <Box sx={{ flex: 1, minHeight: 0, height: '100%' }}>
                <GalaxyMap mapData={mapData} currentSystemId={currentSystemId} onSystemSelect={handleSystemSelect} isLoading={isLoading} />
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <SystemDetailsPanel systemId={selectedSystem.id} onClose={handleCloseDetails} />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex' }}>
            <GalaxyMap mapData={mapData} currentSystemId={currentSystemId} onSystemSelect={handleSystemSelect} isLoading={isLoading} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
